/**
 * Editorial primitives - the visual refresh's reusable parts.
 *
 * These are the four mechanisms that carry most of the "fresher" feeling in
 * the 2026-09 Academy design review, pulled out so they are written once:
 *
 *   Eyebrow         uppercase tracked mono meta above a heading
 *   DisplayHeading  the type tier above Fluent's 24px ceiling
 *   DarkPanel       one Midnight Charcoal band per page, against the cream
 *   ProgressRing    progress as a ring instead of a number
 *
 * EDITORIAL SURFACES ONLY. Same rule as `borderRadius.editorial` and the
 * `display` tokens: heroes, course cards, certificates, landing panels. Not
 * tables, not forms, not filter bars. If it sits next to a column header it
 * does not belong here.
 *
 * Piloted in Academy (docs/design/vera-visual-refresh.md). Written in shared
 * because the intent is Vera-wide, but nothing outside Academy imports them
 * until the pilot is judged.
 */

import type { CSSProperties, ReactNode } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { borderRadius, display, semanticColors, typography, veracityColors } from '../tokens';

const useStyles = makeStyles({
  eyebrow: {
    fontFamily: typography.fontFamilyMono,
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#8A3F22',
    display: 'block',
  },
  eyebrowOnDark: { color: '#FFB89E' },

  display: {
    fontFamily: typography.fontFamily,
    fontWeight: display.fontWeight,
    lineHeight: display.lineHeight,
    letterSpacing: display.letterSpacing,
    margin: 0,
    color: semanticColors.textPrimary,
  },
  d100: { fontSize: display.fontSizeDisplay100 },
  d200: { fontSize: display.fontSizeDisplay200 },
  d300: { fontSize: display.fontSizeDisplay300 },
  displayOnDark: { color: semanticColors.textOnDark },

  panel: {
    backgroundColor: semanticColors.surfaceDark,
    color: semanticColors.textOnDark,
    borderRadius: borderRadius.editorial,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXL}`,
    boxSizing: 'border-box',
  },

  ring: { position: 'relative', flexShrink: 0 },
  ringInner: {
    position: 'absolute',
    inset: '11px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    fontWeight: 700,
    fontFamily: typography.fontFamily,
  },
});

// ---------------------------------------------------------------------------

export interface EyebrowProps {
  children: ReactNode;
  /** Flip to the warm on-dark tint for use inside a DarkPanel. */
  onDark?: boolean;
  className?: string;
}

/** Uppercase tracked mono meta, e.g. "ONBOARDING · WEEK 1". */
export function Eyebrow({ children, onDark, className }: EyebrowProps) {
  const styles = useStyles();
  return (
    <span className={mergeClasses(styles.eyebrow, onDark && styles.eyebrowOnDark, className)}>
      {children}
    </span>
  );
}

export interface DisplayHeadingProps {
  children: ReactNode;
  /** 100 = section, 200 = page hero, 300 = score / certificate. */
  size?: 100 | 200 | 300;
  as?: 'h1' | 'h2' | 'h3' | 'div';
  onDark?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * A heading above Fluent's ramp.
 *
 * `as` is separate from `size` on purpose: heading LEVEL is document
 * structure and screen readers depend on it, while size is presentation. A
 * page's h1 can be a 32px display and a score can be a 64px div.
 */
export function DisplayHeading({
  children,
  size = 200,
  as: Tag = 'h1',
  onDark,
  className,
  style,
}: DisplayHeadingProps) {
  const styles = useStyles();
  const sizeClass = size === 100 ? styles.d100 : size === 300 ? styles.d300 : styles.d200;
  return (
    <Tag
      className={mergeClasses(styles.display, sizeClass, onDark && styles.displayOnDark, className)}
      style={style}
    >
      {children}
    </Tag>
  );
}

export interface DarkPanelProps {
  children: ReactNode;
  className?: string;
}

/**
 * One dark band per page.
 *
 * This is the single biggest perceived change in the refresh and the cheapest:
 * Vera is uniformly cream, so one Midnight Charcoal block gives a page a top
 * and a bottom. Use it once. Two dark bands on one page is a stripe.
 *
 * Contained, not full-bleed. The prototype runs its hero edge to edge, which
 * needs `margin: calc(50% - 50vw)`, and inside the AppShell's own scroll
 * container that overflows horizontally by the scrollbar width whenever one is
 * present. A rounded panel inside the content column reads the same and cannot
 * produce a second scrollbar.
 */
export function DarkPanel({ children, className }: DarkPanelProps) {
  const styles = useStyles();
  return <section className={mergeClasses(styles.panel, className)}>{children}</section>;
}

export interface ProgressRingProps {
  /** 0 to 1. Clamped, so a rounding overshoot cannot draw past a full turn. */
  value: number;
  size?: number;
  /** Center label. Omit for a bare ring. */
  label?: ReactNode;
  onDark?: boolean;
  trackColor?: string;
  fillColor?: string;
}

/**
 * Progress as a ring.
 *
 * A conic-gradient, not an SVG: no viewBox arithmetic, no stroke-dasharray,
 * and it scales with one number. The value is exposed to assistive tech via
 * role="progressbar" because the gradient itself says nothing out loud.
 */
export function ProgressRing({
  value,
  size = 96,
  label,
  onDark,
  trackColor,
  fillColor = veracityColors.daybreakAmber,
}: ProgressRingProps) {
  const styles = useStyles();
  const pct = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
  const track = trackColor ?? (onDark ? '#3A3434' : semanticColors.borderSubtle);
  const inner = onDark ? semanticColors.surfaceDark : semanticColors.surfaceRaised;

  return (
    <div
      className={styles.ring}
      role="progressbar"
      aria-valuenow={Math.round(pct * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `conic-gradient(${fillColor} 0 ${pct}turn, ${track} ${pct}turn 1turn)`,
      }}
    >
      {label !== undefined && (
        <div
          className={styles.ringInner}
          style={{
            backgroundColor: inner,
            color: onDark ? semanticColors.textOnDark : semanticColors.textPrimary,
            fontSize: Math.round(size * 0.2),
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}
